import { useCallback, useMemo, useState } from 'react'
import moment from 'moment-timezone'
import _ from 'lodash'

import {
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import { Box, IconButton, Tooltip } from '@mui/material'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined'

import MapPopup from '/components/passes/map-popup'
import StudioTable, { useStudioTable } from '/components/studio-table'

import DataExportDialog from "../data-export-dialog"
import excelExport from './scans-to-excel-gift'

const ScanTable = ({ scans, loading, templateId, passId }) => {

  const totalDebits = useMemo(() => _.sum(scans.map(d => parseInt(d.amount_debited ?? 0,10))), [scans])
  const totalRefunds = useMemo(() => _.sum(scans.map(d => parseInt(d.amount_credited ?? 0,10))), [scans])
  const totalTopups = useMemo(() => _.sum(scans.map(d => parseInt(d.amount_topped_up ?? 0,10 ))), [scans])

  const [scan,setScan] = useState()
  const [mapOpen,setMapOpen] = useState(false)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)

  const openMap = useCallback(scanRecord => {
    setScan(scanRecord)
    setMapOpen(true)
  }, [])

  const columns = useMemo(() => [
    {
      id: 'timestamp',
      accessorFn: (originalRow) => moment(originalRow.timestamp, "YYYYMMDDHHmmss"),
      header: 'Scanned',
      Cell: ({ cell }) => cell.getValue().format("DD MMM YYYY HH:mm"),
      filterVariant: 'date-range',
      sortingFn: 'datetime',
      size: 180,
      Footer: () => "Totals"
    },
    {
      accessorKey: 'action',
      header: 'Action',
      size: 20,
    },
    {
      accessorKey: 'amount_debited',
      header: 'Debits',
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => moneyFormat("debit",cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalMoneyFormat(totalDebits)
    },
    {
      accessorKey: 'amount_credited',
      header: 'Refunds',
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => moneyFormat("refund",cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalMoneyFormat(totalRefunds)
    },  
    {
      accessorKey: 'amount_topped_up',
      header: 'Topups',
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => moneyFormat("topup",cell.getValue()),

      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalMoneyFormat(totalTopups)
    }, 
    {
      accessorKey: 'reference',
      header: 'Reference',
      filterVariant: 'autocomplete',
      size: 200,
    },
    {
      accessorKey: 'username',
      header: 'Scanned By',
      filterVariant: 'autocomplete',
      size: 200,
    },
    {
      id: 'position',
      accessorFn: (originalRow) => originalRow,
      header: 'Scan Location',
      filterVariant: 'autocomplete',
      size: 400,
      Cell: ({ cell }) => {
        const row = cell.getValue()
        return row.position ? <div className='link' onClick={() => openMap(row)}><RoomOutlinedIcon className='mr-5' />{row.position.x},{row.position.y}</div> : null
      }
    },
  ], [openMap, totalDebits, totalRefunds, totalTopups])

  const table = useStudioTable({
    tableKey: 'scan-history-gift-card',
    columns,
    data: scans,
    state: {
      isLoading: loading
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: (originalRow) => originalRow.id,
    renderToolbarInternalActions: ({ table: tableInstance }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={tableInstance} />
        <MRT_ToggleFiltersButton table={tableInstance} />
        <MRT_ShowHideColumnsButton table={tableInstance} />
        <MRT_ToggleDensePaddingButton table={tableInstance} />
        <MRT_ToggleFullScreenButton table={tableInstance} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
  })

  return (
    <>
      <MapPopup scan={scan} open={mapOpen} setOpen={setMapOpen}/>
      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName={`${templateId}-(${passId})-scan-history`} data={scans} excelExport={excelExport} table={table} />
      <StudioTable table={table} />
    </>
  )

}

export default ScanTable

const moneyFormat = (action, value) => {
  switch(action) {
    case "debit": return value ? <span className="red">£{value?.toFixed(2)}</span> : ""
    case "refund": return value ? <span className="green">£{value?.toFixed(2)}</span> : ""
    case "topup": return value ?  <span className="green">£{value?.toFixed(2)}</span> : ""

    default : return ""
  }
}

const totalMoneyFormat = amount => <span>£{amount?.toFixed(2)}</span>
